import { useEffect, useCallback, useRef } from 'react';

/**
 * @author Matheus
 * Hook para melhorar a navegação por teclado
 * Fornece atalhos de teclado e gerenciamento de foco
 */

interface KeyboardShortcut {
  key: string;
  ctrlKey?: boolean;
  altKey?: boolean;
  shiftKey?: boolean;
  callback: () => void;
  description: string;
}

interface UseKeyboardNavigationOptions {
  shortcuts?: KeyboardShortcut[];
  trapFocus?: boolean;
  autoFocus?: boolean;
  restoreFocus?: boolean;
}

export const useKeyboardNavigation = (options: UseKeyboardNavigationOptions = {}) => {
  const {
    shortcuts = [],
    trapFocus = false,
    autoFocus = false,
    restoreFocus = false
  } = options;

  const containerRef = useRef<HTMLElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Função para obter elementos focáveis
  const getFocusableElements = useCallback((container: HTMLElement) => {
    const focusableSelectors = [
      'button:not([disabled])',
      'input:not([disabled])',
      'textarea:not([disabled])',
      'select:not([disabled])',
      'a[href]',
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable="true"]'
    ].join(', ');

    return Array.from(container.querySelectorAll(focusableSelectors)) as HTMLElement[];
  }, []);

  // Função para navegar entre elementos
  const navigateToElement = useCallback((direction: 'next' | 'previous' | 'first' | 'last') => {
    if (!containerRef.current) return;

    const focusableElements = getFocusableElements(containerRef.current);
    if (focusableElements.length === 0) return;

    const currentIndex = focusableElements.indexOf(document.activeElement as HTMLElement);
    let nextIndex: number;

    switch (direction) {
      case 'next':
        nextIndex = currentIndex < focusableElements.length - 1 ? currentIndex + 1 : 0;
        break;
      case 'previous':
        nextIndex = currentIndex > 0 ? currentIndex - 1 : focusableElements.length - 1;
        break;
      case 'first':
        nextIndex = 0;
        break;
      case 'last':
        nextIndex = focusableElements.length - 1;
        break;
      default:
        return;
    }

    focusableElements[nextIndex]?.focus();
  }, [getFocusableElements]);

  // Handler para atalhos de teclado
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    // Verificar se event.key existe
    if (!event.key) return;
    
    // Verificar atalhos personalizados
    for (const shortcut of shortcuts) {
      const ctrlMatch = shortcut.ctrlKey === undefined || shortcut.ctrlKey === event.ctrlKey;
      const altMatch = shortcut.altKey === undefined || shortcut.altKey === event.altKey;
      const shiftMatch = shortcut.shiftKey === undefined || shortcut.shiftKey === event.shiftKey;
      const keyMatch = shortcut.key.toLowerCase() === event.key.toLowerCase();

      if (ctrlMatch && altMatch && shiftMatch && keyMatch) {
        event.preventDefault();
        shortcut.callback();
        return;
      }
    }

    // Navegação por teclado padrão
    if (trapFocus && containerRef.current) {
      switch (event.key) {
        case 'Tab':
          event.preventDefault();
          navigateToElement(event.shiftKey ? 'previous' : 'next');
          break;
        case 'Home':
          if (event.ctrlKey) {
            event.preventDefault();
            navigateToElement('first');
          }
          break;
        case 'End':
          if (event.ctrlKey) {
            event.preventDefault();
            navigateToElement('last');
          }
          break;
        case 'Escape':
          if (restoreFocus && previousFocusRef.current) {
            previousFocusRef.current.focus();
          }
          break;
      }
    }
  }, [shortcuts, trapFocus, navigateToElement, restoreFocus]);

  // Função para focar no primeiro elemento
  const focusFirst = useCallback(() => {
    navigateToElement('first');
  }, [navigateToElement]);

  // Função para focar no último elemento
  const focusLast = useCallback(() => {
    navigateToElement('last');
  }, [navigateToElement]);

  // Função para salvar o foco atual
  const saveFocus = useCallback(() => {
    previousFocusRef.current = document.activeElement as HTMLElement;
  }, []);

  // Função para restaurar o foco
  const restoreFocusToSaved = useCallback(() => {
    if (previousFocusRef.current) {
      previousFocusRef.current.focus();
    }
  }, []);

  // Efeito para configurar event listeners
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Salvar foco anterior se necessário
    if (restoreFocus) {
      saveFocus();
    }

    // Auto focus no primeiro elemento
    if (autoFocus) {
      const focusableElements = getFocusableElements(container);
      if (focusableElements.length > 0) {
        focusableElements[0].focus();
      }
    }

    // Adicionar event listener
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      
      // Restaurar foco se necessário
      if (restoreFocus && previousFocusRef.current) {
        previousFocusRef.current.focus();
      }
    };
  }, [handleKeyDown, autoFocus, restoreFocus, getFocusableElements, saveFocus]);

  return {
    containerRef,
    focusFirst,
    focusLast,
    saveFocus,
    restoreFocus: restoreFocusToSaved,
    navigateToElement
  };
};

// Hook para atalhos globais
export const useGlobalKeyboardShortcuts = (shortcuts: KeyboardShortcut[]) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Verificar se event.key existe
      if (!event.key) return;
      
      for (const shortcut of shortcuts) {
        const ctrlMatch = shortcut.ctrlKey === undefined || shortcut.ctrlKey === event.ctrlKey;
        const altMatch = shortcut.altKey === undefined || shortcut.altKey === event.altKey;
        const shiftMatch = shortcut.shiftKey === undefined || shortcut.shiftKey === event.shiftKey;
        const keyMatch = shortcut.key.toLowerCase() === event.key.toLowerCase();

        if (ctrlMatch && altMatch && shiftMatch && keyMatch) {
          event.preventDefault();
          shortcut.callback();
          return;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [shortcuts]);
};

// Atalhos padrão do sistema
export const getDefaultShortcuts = (callbacks: {
  openSearch?: () => void;
  openSettings?: () => void;
  openAccessibility?: () => void;
  goToDashboard?: () => void;
  goToKanban?: () => void;
  goToConversations?: () => void;
}): KeyboardShortcut[] => [
  {
    key: 'k',
    ctrlKey: true,
    callback: callbacks.openSearch || (() => {}),
    description: 'Abrir busca (Ctrl+K)'
  },
  {
    key: ',',
    ctrlKey: true,
    callback: callbacks.openSettings || (() => {}),
    description: 'Abrir configurações (Ctrl+,)'
  },
  {
    key: 'a',
    ctrlKey: true,
    altKey: true,
    callback: callbacks.openAccessibility || (() => {}),
    description: 'Abrir acessibilidade (Ctrl+Alt+A)'
  },
  {
    key: '1',
    ctrlKey: true,
    callback: callbacks.goToDashboard || (() => {}),
    description: 'Ir para Dashboard (Ctrl+1)'
  },
  {
    key: '2',
    ctrlKey: true,
    callback: callbacks.goToKanban || (() => {}),
    description: 'Ir para Kanban (Ctrl+2)'
  },
  {
    key: '3',
    ctrlKey: true,
    callback: callbacks.goToConversations || (() => {}),
    description: 'Ir para Conversas (Ctrl+3)'
  }
];